import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { authAPI, watchlistAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';
import { useHaptic } from '../hooks';
import toast from 'react-hot-toast';
import i18next from 'i18next';
import {
  HiOutlineUser,
  HiOutlineMail,
  HiOutlineKey,
  HiOutlineGlobe,
  HiOutlineDeviceMobile,
  HiOutlineCheck,
} from 'react-icons/hi';

function MobileProfileEdit() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const { light } = useHaptic();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    newPassword: '',
    confirmPassword: '',
    preferredLanguage: 'pt-BR',
    systemLanguage: 'pt-BR',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        newPassword: '',
        confirmPassword: '',
        preferredLanguage: user.preferred_language || 'pt-BR',
        systemLanguage: user.system_language || 'pt-BR',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    light();

    try {
      // Validate passwords
      if (formData.newPassword || formData.confirmPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          toast.error(t('profile.passwordsDontMatch'));
          setLoading(false);
          return;
        }
        if (formData.newPassword.length < 6) {
          toast.error(t('profile.passwordTooShort'));
          setLoading(false);
          return;
        }
      }

      const updateData = {};
      if (formData.username !== user?.username) updateData.username = formData.username;
      if (formData.email !== user?.email) updateData.email = formData.email;
      if (formData.newPassword) updateData.password = formData.newPassword;
      if (formData.preferredLanguage !== user?.preferred_language) {
        updateData.preferred_language = formData.preferredLanguage;
      }
      if (formData.systemLanguage !== user?.system_language) {
        updateData.system_language = formData.systemLanguage;
      }

      if (Object.keys(updateData).length === 0) {
        toast.success(t('profile.noChanges'));
        navigate('/profile');
        return;
      }

      await authAPI.updateProfile(updateData);

      // Update i18n if system language changed
      if (updateData.system_language) {
        i18next.changeLanguage(formData.systemLanguage);
        localStorage.setItem('systemLanguage', formData.systemLanguage);
      }

      // Sync content if language changed
      if (updateData.preferred_language) {
        toast.success(t('profile.profileUpdatedSyncing'));
        try {
          await watchlistAPI.syncLanguage();
          toast.success(t('profile.contentSynced'));
        } catch (syncError) {
          console.error('Sync error:', syncError);
        }
      } else {
        toast.success(t('profile.profileUpdated'));
      }

      // Refresh user data
      const response = await authAPI.getMe();
      setUser(response.data);

      navigate('/profile');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const LanguageOption = ({ value, label, flag, selected, onClick }) => (
    <button
      type="button"
      onClick={() => {
        light();
        onClick();
      }}
      className={`mobile-profile-language ${selected ? 'active' : ''}`}
    >
      <img src={flag} alt={label} />
      <span>{label}</span>
      {selected && <HiOutlineCheck size={20} />}
    </button>
  );

  return (
    <div className="mobile-profile-edit">
      <form className="mobile-profile-edit__form" onSubmit={handleSubmit}>
        {/* Account Section */}
        <div className="mobile-profile-edit__section">
          <div className="mobile-profile-edit__section-title">
            <HiOutlineUser size={18} />
            {t('profile.accountInfo')}
          </div>
          
          <div className="mobile-profile-edit__field">
            <label>
              <HiOutlineUser size={16} />
              {t('common.username')}
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder={t('profile.enterUsername')}
            />
          </div>

          <div className="mobile-profile-edit__field">
            <label>
              <HiOutlineMail size={16} />
              {t('common.email')}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('profile.enterEmail')}
            />
          </div>
        </div>

        {/* Password Section */}
        <div className="mobile-profile-edit__section">
          <div className="mobile-profile-edit__section-title">
            <HiOutlineKey size={18} />
            {t('profile.changePassword')}
          </div>
          
          <div className="mobile-profile-edit__field">
            <label>{t('profile.newPassword')}</label>
            <input
              type="password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder={t('profile.enterNewPassword')}
            />
          </div>

          <div className="mobile-profile-edit__field">
            <label>{t('profile.confirmPassword')}</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('profile.confirmNewPassword')}
            />
          </div>
        </div>

        {/* Content Language Section */}
        <div className="mobile-profile-edit__section">
          <div className="mobile-profile-edit__section-title">
            <HiOutlineGlobe size={18} />
            {t('profile.languagePreference')}
          </div>
          <div className="mobile-profile-edit__language-grid">
            <LanguageOption
              value="pt-BR"
              label="Português"
              flag="https://flagcdn.com/w40/br.png"
              selected={formData.preferredLanguage === 'pt-BR'}
              onClick={() => setFormData({ ...formData, preferredLanguage: 'pt-BR' })}
            />
            <LanguageOption
              value="en"
              label="English"
              flag="https://flagcdn.com/w40/us.png"
              selected={formData.preferredLanguage === 'en'}
              onClick={() => setFormData({ ...formData, preferredLanguage: 'en' })}
            />
          </div>
        </div>

        {/* System Language Section */}
        <div className="mobile-profile-edit__section">
          <div className="mobile-profile-edit__section-title">
            <HiOutlineDeviceMobile size={18} />
            {t('profile.systemLanguage')}
          </div>
          <div className="mobile-profile-edit__radio-group">
            {/* PT-BR Option */}
            <label
              className="mobile-profile-edit__radio-option"
              onClick={() => {
                light();
                setFormData({ ...formData, systemLanguage: 'pt-BR' });
              }}
            >
              <div className={`mobile-profile-edit__radio ${formData.systemLanguage === 'pt-BR' ? 'active' : ''}`}>
                {formData.systemLanguage === 'pt-BR' && <div className="mobile-profile-edit__radio-dot" />}
              </div>
              <input
                type="radio"
                name="systemLanguage"
                value="pt-BR"
                checked={formData.systemLanguage === 'pt-BR'}
                onChange={() => setFormData({ ...formData, systemLanguage: 'pt-BR' })}
              />
              <span>PT-BR</span>
            </label>

            {/* EN-US Option */}
            <label
              className="mobile-profile-edit__radio-option"
              onClick={() => {
                light();
                setFormData({ ...formData, systemLanguage: 'en-US' });
              }}
            >
              <div className={`mobile-profile-edit__radio ${formData.systemLanguage === 'en-US' ? 'active' : ''}`}>
                {formData.systemLanguage === 'en-US' && <div className="mobile-profile-edit__radio-dot" />}
              </div>
              <input
                type="radio"
                name="systemLanguage"
                value="en-US"
                checked={formData.systemLanguage === 'en-US'}
                onChange={() => setFormData({ ...formData, systemLanguage: 'en-US' })}
              />
              <span>EN-US</span>
            </label>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="mobile-profile-edit__footer">
          <div className="mobile-profile-edit__buttons">
            <button
              type="button"
              className="mobile-profile-edit__btn mobile-profile-edit__btn--cancel"
              onClick={() => navigate('/profile')}
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="mobile-profile-edit__btn mobile-profile-edit__btn--save"
              disabled={loading}
            >
              {loading ? t('profile.saving') : t('profile.saveChanges')}
            </button>
          </div>
        </div>

        {/* Bottom spacer */}
        <div className="mobile-profile-edit__bottom-spacer" />
      </form>
    </div>
  );
}

export default MobileProfileEdit;
